import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, FormControl, FormLabel, Input, Select, Textarea,
  SimpleGrid, VStack, Text, Badge, HStack, useToast, Divider, Alert,
  AlertIcon, AlertDescription, NumberInput, NumberInputField, Box,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import type { CatalogoInfraccion, ClasificacionInfraccion } from './juzgadoTypes';
import juzgadoApi from '../../services/juzgadoApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  infraccion: any;
  onGuardado: () => void;
}

export default function ModalCalificarInfraccion({ isOpen, onClose, infraccion, onGuardado }: Props) {
  const [catalogo, setCatalogo] = useState<CatalogoInfraccion[]>([]);
  const [catalogoSelId, setCatalogoSelId] = useState<number | ''>('');
  const [articuloManual, setArticuloManual] = useState('');
  const [tipoFaltaManual, setTipoFaltaManual] = useState('');
  const [gravedadManual, setGravedadManual] = useState('');
  const [motivoCambio, setMotivoCambio] = useState('');
  const [iaAceptada, setIaAceptada] = useState<boolean | null>(null);
  const [montoSancion, setMontoSancion] = useState<number>(0);
  const [medidaAdmin, setMedidaAdmin] = useState('');
  const [sugerencia, setSugerencia] = useState<any>(null);
  const [clasificacion, setClasificacion] = useState<ClasificacionInfraccion | null>(null);
  const [etapa, setEtapa] = useState<'clasificar' | 'sancionar' | 'resolucion'>('clasificar');
  const [saving, setSaving] = useState(false);
  // Descuento manual
  const [mostrarDescuento, setMostrarDescuento] = useState(false);
  const [descuento, setDescuento] = useState({ porcentaje: 0, motivo: 'Convenio', motivoDetalle: '' });
  const toast = useToast();

  const handleAplicarDescuento = async () => {
    const sancion = (clasificacion as any)?.SancionInfraccion;
    if (!sancion) return;
    if (!descuento.motivo) return toast({ title: 'Motivo obligatorio', status: 'warning' });
    if (!descuento.porcentaje || descuento.porcentaje <= 0) return toast({ title: 'Porcentaje inválido', status: 'warning' });
    setSaving(true);
    try {
      const r = await juzgadoApi.aplicarDescuentoSancion(sancion.id, {
        tipo: 'MANUAL',
        porcentaje: descuento.porcentaje,
        motivo: descuento.motivo,
        motivoDetalle: descuento.motivoDetalle || undefined,
      });
      toast({ title: `Descuento aplicado. Nuevo monto: $${r.montoFinalActualizado}`, status: 'success', duration: 5000 });
      setMostrarDescuento(false);
      setDescuento({ porcentaje: 0, motivo: 'Convenio', motivoDetalle: '' });
      onGuardado();
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !infraccion) return;
    juzgadoApi.listarCatalogosInfraccion().then(setCatalogo).catch(() => {});
    const cl = infraccion.ClasificacionInfraccion;
    if (cl) {
      setClasificacion(cl);
      setCatalogoSelId(cl.catalogoInfraccionId ?? '');
      setArticuloManual(cl.articuloFinal ?? '');
      setTipoFaltaManual(cl.tipoFaltaFinal ?? '');
      setGravedadManual(cl.gravedadFinal ?? '');
      if (cl.sugerenciaIaJson) {
        try { setSugerencia(JSON.parse(cl.sugerenciaIaJson)); } catch {}
      }
      if (cl.SancionInfraccion) {
        setMontoSancion(Number(cl.SancionInfraccion.montoOriginal));
        setMedidaAdmin(cl.SancionInfraccion.medidaAdministrativa ?? '');
        setEtapa(cl.SancionInfraccion.ResolucionSancion ? 'resolucion' : 'sancionar');
      } else if (cl.estatusValidacion === 'VALIDADA') {
        setEtapa('sancionar');
      }
    } else {
      setClasificacion(null);
      setEtapa('clasificar');
      setSugerencia(null);
      setMontoSancion(Number(infraccion.monto || 0));
    }
  }, [isOpen, infraccion]);

  const handleSelCatalogo = (id: number | '') => {
    setCatalogoSelId(id);
    if (id) {
      const item = catalogo.find(c => c.id === id);
      if (item) {
        setArticuloManual(item.articulo);
        setTipoFaltaManual(item.tipoFalta);
        setGravedadManual(item.gravedad);
        setMontoSancion(Number(item.montoBase));
      }
    }
  };

  const handleClasificar = async () => {
    if (!articuloManual) return toast({ title: 'Artículo infringido requerido', status: 'warning' });
    setSaving(true);
    try {
      const data = await juzgadoApi.clasificarInfraccion(infraccion.id, {
        catalogoInfraccionId: catalogoSelId || null,
        articuloFinal: articuloManual,
        tipoFaltaFinal: tipoFaltaManual,
        gravedadFinal: gravedadManual,
        iaAceptada,
        motivoCambio: motivoCambio || null,
      });
      setClasificacion(data);
      setEtapa('sancionar');
      toast({ title: 'Infracción clasificada y validada', status: 'success' });
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCalificarSancion = async () => {
    if (!montoSancion || montoSancion <= 0) return toast({ title: 'Monto de sanción requerido', status: 'warning' });
    setSaving(true);
    try {
      await juzgadoApi.calificarSancion(infraccion.id, {
        montoOriginal: montoSancion,
        medidaAdministrativa: medidaAdmin || null,
      });
      setEtapa('resolucion');
      toast({ title: 'Sanción aplicada', status: 'success' });
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleGenerarResolucion = async () => {
    setSaving(true);
    try {
      const resultado = await juzgadoApi.generarResolucion(infraccion.id);
      toast({ title: `Resolución generada: ${resultado.resolucion.folioResolucion}`, status: 'success', duration: 6000 });
      onGuardado();
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!infraccion) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg="#5b182b" color="white" borderTopRadius="md">
          Calificar Infracción — {infraccion.folio}
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody pt={4}>
          {/* Datos de la infracción */}
          <Box p={3} bg="gray.50" borderRadius="md" mb={4} fontSize="sm">
            <SimpleGrid columns={2} spacing={2}>
              <Text><strong>Ciudadano:</strong> {infraccion.ciudadano ?? '—'}</Text>
              <Text><strong>Fecha:</strong> {infraccion.fechaInfraccion ? new Date(infraccion.fechaInfraccion).toLocaleDateString('es-MX') : '—'}</Text>
              <Text><strong>Lugar:</strong> {infraccion.lugarInfraccion ?? '—'}</Text>
              <Text><strong>Descripción:</strong> {infraccion.descripcion ?? '—'}</Text>
            </SimpleGrid>
          </Box>

          {/* Sugerencia IA */}
          {sugerencia && (
            <Alert status={sugerencia.confianza === 'ALTA' ? 'success' : 'warning'} mb={4} borderRadius="md">
              <AlertIcon />
              <AlertDescription fontSize="sm">
                <Text fontWeight="semibold">Sugerencia IA (confianza: {sugerencia.confianza})</Text>
                {sugerencia.articuloSugerido && <Text>Artículo: {sugerencia.articuloSugerido}</Text>}
                {sugerencia.tipoFaltaSugerido && <Text>Tipo: {sugerencia.tipoFaltaSugerido}</Text>}
                {sugerencia.montoSugerido && <Text>Monto sugerido: ${sugerencia.montoSugerido}</Text>}
                <Text fontSize="xs" color="gray.500" mt={1}>⚠️ Solo orientativo. La decisión final debe ser validada por un funcionario autorizado.</Text>
              </AlertDescription>
            </Alert>
          )}

          {/* ETAPA 1: Clasificación */}
          {(etapa === 'clasificar' || clasificacion) && (
            <VStack spacing={3} align="stretch" mb={4}>
              <HStack>
                <Text fontWeight="semibold" color="#5b182b">Etapa 1: Clasificación</Text>
                {clasificacion?.estatusValidacion === 'VALIDADA' && <Badge colorScheme="green">Validada</Badge>}
              </HStack>
              <FormControl>
                <FormLabel fontSize="sm">Seleccionar del catálogo</FormLabel>
                <Select value={catalogoSelId} onChange={e => handleSelCatalogo(e.target.value ? Number(e.target.value) : '')} borderColor="#bb8c52" isDisabled={etapa !== 'clasificar'}>
                  <option value="">Seleccionar artículo...</option>
                  {catalogo.map(c => (
                    <option key={c.id} value={c.id}>{c.articulo}{c.fraccion ? ` Frac. ${c.fraccion}` : ''} — {c.descripcion.substring(0, 60)}...</option>
                  ))}
                </Select>
              </FormControl>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Artículo infringido</FormLabel>
                  <Input value={articuloManual} onChange={e => setArticuloManual(e.target.value)} isReadOnly={etapa !== 'clasificar'} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Tipo de falta</FormLabel>
                  <Input value={tipoFaltaManual} onChange={e => setTipoFaltaManual(e.target.value)} isReadOnly={etapa !== 'clasificar'} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Gravedad</FormLabel>
                  <Select value={gravedadManual} onChange={e => setGravedadManual(e.target.value)} isDisabled={etapa !== 'clasificar'} borderColor="#bb8c52">
                    <option value="">Seleccionar...</option>
                    <option value="Leve">Leve</option>
                    <option value="Moderada">Moderada</option>
                    <option value="Grave">Grave</option>
                    <option value="Muy grave">Muy grave</option>
                  </Select>
                </FormControl>
              </SimpleGrid>
              {etapa === 'clasificar' && sugerencia && (
                <FormControl>
                  <FormLabel fontSize="sm">¿Se aceptó la sugerencia de IA?</FormLabel>
                  <Select value={iaAceptada === null ? '' : String(iaAceptada)} onChange={e => setIaAceptada(e.target.value === 'true' ? true : e.target.value === 'false' ? false : null)} borderColor="#bb8c52">
                    <option value="">Sin aplicar</option>
                    <option value="true">Sí, se aceptó</option>
                    <option value="false">No, se modificó</option>
                  </Select>
                </FormControl>
              )}
              {etapa === 'clasificar' && iaAceptada === false && (
                <FormControl>
                  <FormLabel fontSize="sm">Motivo del cambio sobre sugerencia IA</FormLabel>
                  <Textarea value={motivoCambio} onChange={e => setMotivoCambio(e.target.value)} rows={2} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                </FormControl>
              )}
              {etapa === 'clasificar' && (
                <HStack justify="flex-end">
                  <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} size="sm" isLoading={saving} onClick={handleClasificar}>
                    Validar Clasificación
                  </Button>
                </HStack>
              )}
            </VStack>
          )}

          <Divider />

          {/* ETAPA 2: Sanción */}
          {(etapa === 'sancionar' || etapa === 'resolucion') && (
            <VStack spacing={3} align="stretch" mt={4} mb={4}>
              <HStack>
                <Text fontWeight="semibold" color="#5b182b">Etapa 2: Calificación de Sanción</Text>
                {(clasificacion as any)?.SancionInfraccion && <Badge colorScheme="purple">Sancionada</Badge>}
              </HStack>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Monto de la sanción ($)</FormLabel>
                  <NumberInput value={montoSancion} min={0} isReadOnly={etapa !== 'sancionar'} onChange={(_, n) => setMontoSancion(n)}>
                    <NumberInputField borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </NumberInput>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Medida administrativa alternativa</FormLabel>
                  <Input value={medidaAdmin} onChange={e => setMedidaAdmin(e.target.value)} isReadOnly={etapa !== 'sancionar'} placeholder="Ej. Trabajo comunitario" borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                </FormControl>
              </SimpleGrid>
              {etapa === 'sancionar' && (
                <HStack justify="flex-end">
                  <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} size="sm" isLoading={saving} onClick={handleCalificarSancion}>
                    Aplicar Sanción
                  </Button>
                </HStack>
              )}

              {/* Descuento manual controlado */}
              {(clasificacion as any)?.SancionInfraccion && (
                <Box mt={2}>
                  <Button size="xs" variant="outline" colorScheme="orange" onClick={() => setMostrarDescuento(!mostrarDescuento)}>
                    {mostrarDescuento ? 'Cancelar descuento' : 'Aplicar descuento manual'}
                  </Button>
                  {mostrarDescuento && (
                    <Box p={3} mt={2} border="1px solid" borderColor="orange.200" borderRadius="md">
                      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
                        <FormControl isRequired>
                          <FormLabel fontSize="sm">Porcentaje (%)</FormLabel>
                          <NumberInput value={descuento.porcentaje} min={0} max={100} onChange={(_, n) => setDescuento(p => ({ ...p, porcentaje: n }))}>
                            <NumberInputField borderColor="#bb8c52" />
                          </NumberInput>
                        </FormControl>
                        <FormControl isRequired>
                          <FormLabel fontSize="sm">Motivo</FormLabel>
                          <Select value={descuento.motivo} onChange={e => setDescuento(p => ({ ...p, motivo: e.target.value }))} borderColor="#bb8c52">
                            <option value="Convenio">Convenio</option>
                            <option value="Situación especial">Situación especial</option>
                            <option value="Autorización administrativa">Autorización administrativa</option>
                            <option value="Otro">Otro</option>
                          </Select>
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="sm">Detalle</FormLabel>
                          <Input value={descuento.motivoDetalle} onChange={e => setDescuento(p => ({ ...p, motivoDetalle: e.target.value }))} borderColor="#bb8c52" />
                        </FormControl>
                      </SimpleGrid>
                      <HStack justify="flex-end" mt={2}>
                        <Button size="sm" colorScheme="orange" isLoading={saving} onClick={handleAplicarDescuento}>Confirmar Descuento</Button>
                      </HStack>
                    </Box>
                  )}
                </Box>
              )}
            </VStack>
          )}

          <Divider />

          {/* ETAPA 3: Resolución */}
          {(etapa === 'resolucion') && (
            <VStack spacing={3} align="stretch" mt={4}>
              <HStack>
                <Text fontWeight="semibold" color="#5b182b">Etapa 3: Generar Resolución</Text>
                {(clasificacion as any)?.SancionInfraccion?.ResolucionSancion && <Badge colorScheme="green">Resolución generada</Badge>}
              </HStack>
              {!(clasificacion as any)?.SancionInfraccion?.ResolucionSancion && (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription fontSize="sm">
                    La resolución incluirá automáticamente el descuento del 50% si el pago se realiza en los primeros 5 días naturales.
                  </AlertDescription>
                </Alert>
              )}
              {(clasificacion as any)?.SancionInfraccion?.ResolucionSancion ? (
                <Box p={3} bg="green.50" borderRadius="md" fontSize="sm">
                  <Text fontWeight="semibold">Folio: {(clasificacion as any).SancionInfraccion.ResolucionSancion.folioResolucion}</Text>
                  <Text>Monto final: ${Number((clasificacion as any).SancionInfraccion.ResolucionSancion.montoFinal).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</Text>
                  <Text>Estatus: <Badge colorScheme="blue">{(clasificacion as any).SancionInfraccion.ResolucionSancion.estatus}</Badge></Text>
                </Box>
              ) : (
                <HStack justify="flex-end">
                  <Button colorScheme="green" size="sm" isLoading={saving} onClick={handleGenerarResolucion}>
                    Generar Resolución Oficial
                  </Button>
                </HStack>
              )}
              {(clasificacion as any)?.SancionInfraccion?.ResolucionSancion && (
                <HStack justify="flex-end" mt={2}>
                  <Button as="a" href={juzgadoApi.pdfInformeSancion(infraccion.id)} target="_blank"
                    colorScheme="red" size="sm" variant="outline">
                    Descargar Informe de Sanción (PDF)
                  </Button>
                </HStack>
              )}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter borderTopWidth={1}>
          <Button variant="ghost" onClick={onClose}>Cerrar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
